import React from 'react'
import { motion } from 'framer-motion'
import { Phone, Mail, MapPin, Clock } from 'lucide-react'
import { CONTACT_INFO } from '../constants/stats'

const Contact = () => {
  const contactInfo = CONTACT_INFO

  // Маппинг иконок
  const iconMap = {
    Phone,
    Mail,
    MapPin,
    Clock
  }

  return (
    <div className="page-padding">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 text-white py-20">
        <div className="container-custom text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
          >
            Свяжитесь с нами
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed"
          >
            Готовы защитить ваш автомобиль от коррозии?
            Свяжитесь с нами любым удобным способом
          </motion.p>
        </div>
      </section>

      {/* Contact Info & FAQ */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-secondary-900 mb-8">
                Контактная информация
              </h2>

              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={info.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="flex items-start space-x-4"
                  >
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                      {React.createElement(iconMap[info.icon as keyof typeof iconMap], {
                        className: "w-6 h-6 text-primary-600"
                      })}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-secondary-900 mb-1">
                        {info.title}
                      </h3>
                      {info.action ? (
                        <a
                          href={info.action}
                          target={info.action.startsWith('http') ? '_blank' : undefined}
                          rel={info.action.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="text-lg font-bold text-primary-600 hover:text-primary-700 transition-colors duration-200"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <div className="text-lg font-bold text-primary-600">
                          {info.value}
                        </div>
                      )}
                      <p className="text-secondary-600 text-sm">
                        {info.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* FAQ Section */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-secondary-900 mb-8">
                Часто задаваемые вопросы
              </h2>
              <p className="text-lg text-secondary-600 mb-8">
                Ответы на самые популярные вопросы наших клиентов
              </p>

              <div className="space-y-6">
                {[
                  {
                    question: 'Сколько времени занимает обработка?',
                    answer: 'Время обработки зависит от выбранной услуги и размера автомобиля. Обычно это занимает 1-2 дня.'
                  },
                  {
                    question: 'Какая гарантия на работы?',
                    answer: 'Мы предоставляем гарантию от 1 до 5 лет в зависимости от вида обработки и используемых материалов.'
                  },
                  {
                    question: 'Можно ли приехать без записи?',
                    answer: 'Рекомендуем записываться заранее, чтобы мы могли подготовить все необходимое и избежать ожидания.'
                  },
                  {
                    question: 'Какие материалы вы используете?',
                    answer: 'Мы используем только сертифицированные материалы ведущих производителей: Dinitrol, WURTH и другие.'
                  }
                ].map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="card"
                  >
                    <h3 className="text-lg font-semibold text-secondary-900 mb-3">
                      {faq.question}
                    </h3>
                    <p className="text-secondary-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Contact
